<template>
  <MeasurementBase
    :title="title"
    :info="info"
    :value="value"
    :color="
      color !== undefined
        ? color
        : value && 'number' in value && colorScale
        ? getColorForValue(colorScale, value.number).toHex()
        : color
    "
    :showOnMapEnabled="showOnMapEnabled"
    :showOnMap="showOnMap"
    :isLoading="value?.isLoading ?? true"
    :fullscreen="fullscreen"
    @toggleShowOnMap="toggleShowOnMap"
  >
    <template v-if="$slots.details" #details>
      <slot name="details"></slot>
    </template>
  </MeasurementBase>
  <ColorLegendComponent
    v-if="
      showOnMap &&
      showOnMapEnabled &&
      !fullscreen &&
      (!feature?.children || feature.children.length === 0) &&
      value &&
      'number' in value &&
      value.number !== undefined
    "
    :title="title"
    :width="colorLegend?.width"
    :height="colorLegend?.height"
    :padding="colorLegend?.padding"
    :format="colorLegend?.format ?? value?.format"
    :labelStart="colorLegend?.labelStart?.[locale]"
    :labelEnd="colorLegend?.labelEnd?.[locale]"
    :colorScale="colorScale"
    :mean="value.number"
    :hideMean="colorLegend?.hideMean"
  />
  <FeatureChildren
    v-if="
      showOnMap &&
      showOnMapEnabled &&
      !fullscreen &&
      feature &&
      value &&
      'number' in value &&
      value.number !== undefined
    "
    :value="value.number"
    :feature="`home/*/*/*/data/*/${feature.name}`"
  />
</template>
<script lang="ts" setup>
import MeasurementBase, { Value } from './MeasurementBase.vue'
import { computed, inject, onMounted, onUnmounted, toRefs, watch } from 'vue'
import { FormatOptions, formatter } from '../formatter'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import useColoredEntities from '@/composables/useColoredEntities'
import { useGeneralStore } from '@/pinia'
import { useI18n } from 'vue-i18n'
import md5 from 'md5'
import { sharedDataKey } from '../../types'
import ColorLegendComponent from '@/design-system/atoms/ColorLegend.vue'
import FeatureChildren from '@/components/FeatureChildren.vue'
import { Feature } from '@/pinia/feature'
import { storeToRefs } from 'pinia'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'

export type Coloring = {
  id: string
  meta?: any
  value: number
}

export type ColoredEntities = {
  colorings?: Coloring[]
  enabledEntityLevels?: EntityLevel[]
}

export type ColorLegend = {
  width?: number
  height?: number
  padding?: number
  hideMean?: boolean
  labelStart?: LocaleString
  labelEnd?: LocaleString
  format?: FormatOptions
  errorMessage?: string
}

type Props = {
  title: string
  name?: string
  info?: string
  value?: { isLoading?: boolean } & Value
  feature?: Feature
  colorScale?: ColorScaleConfig
  color?: string
  coloredEntities?: ColoredEntities
  colorLegend?: ColorLegend
  initialShowOnMap?: boolean
  absoluteColorScale?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  info: undefined,
  name: undefined,
  value: undefined,
  color: undefined,
  feature: undefined,
  coloredEntities: undefined,
  initialShowOnMap: false,
  absoluteColorScale: true,
  colorScale: undefined,
  colorLegend: () => ({
    height: 60,
    padding: 20,
    hideMean: false
  }),
  descriptionCaption: () => ({ sv: 'Detaljer', en: 'Details' })
})

const piniaStore = useGeneralStore()

const { value, coloredEntities } = toRefs(props)

const { locale } = useI18n()

const coloredEntitiesStore = useColoredEntities()

const sharedData = inject(sharedDataKey)

const fullscreen = computed(() => sharedData?.fullscreen ?? false)

const { entityLevel } = storeToRefs(useEntityLevel())

const showOnMap = computed(() => coloredEntitiesStore.currentKey === key.value)

const hasColorings = computed(() => {
  return (
    (coloredEntities.value?.colorings && coloredEntities.value.colorings.length > 1) ||
    props.absoluteColorScale
  )
})

const onAllowedEntityLevel = computed(() => {
  return (
    (coloredEntities?.value?.enabledEntityLevels &&
      coloredEntities.value.enabledEntityLevels.includes(entityLevel.value)) ||
    (coloredEntities?.value && !coloredEntities.value.enabledEntityLevels)
  )
})

const showOnMapEnabled = computed(() => (hasColorings.value && onAllowedEntityLevel.value) ?? false)

const coloredEntitiesState = computed(() => {
  return coloredEntities.value?.colorings
})

const key = computed<string>(() => md5(props.name + props.title + props.feature?.name))

watch(
  coloredEntitiesState,
  () => {
    if (showOnMap.value) {
      coloredEntitiesStore.set({
        key: key.value,
        data: coloredEntitiesState.value,
        colorScale: props.colorScale,
        title: props.title,
        unit: '',
        formatter: (v) =>
          formatter(
            v,
            locale.value,
            value.value && 'format' in value.value ? value.value.format : undefined
          )
      })
    }
  },
  { deep: true }
)

onMounted(() => {
  if (
    (props.initialShowOnMap && !coloredEntitiesStore.currentKey) ||
    coloredEntitiesStore.currentKey === key.value
  ) {
    setShowOnMap(true)
  }
})

onUnmounted(() => {
  // coloredEntitiesStore.reset()
})

function setShowOnMap(state: boolean) {
  if (state) {
    coloredEntitiesStore.reset(true)
    coloredEntitiesStore.set({
      key: key.value,
      data: coloredEntitiesState.value,
      colorScale: props.colorScale,
      title: props.title,
      unit: '',
      formatter: (v) =>
        formatter(
          v,
          locale.value,
          value.value && 'format' in value.value ? value.value.format : undefined
        )
    })
    piniaStore.setSelectedAnalysisKPI(props.feature?.name)
  } else {
    coloredEntitiesStore.reset(true)
  }
}

function toggleShowOnMap() {
  setShowOnMap(!showOnMap.value)
}
</script>
